import { Component, OnInit, signal, computed, HostListener } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AccountService } from '../services/account.service';
import { inject } from '@angular/core';
import { NotificationsService } from '@core/services/notifications.service';
import { ApiDataResponse } from '@shared/model/api-response.model';
import { CreateAccountData_Result } from '@typeDefs/account/responses/account';
import { CreateAccountFormData } from './create-account-form-data';

@Component({
  selector: 'app-create-account',
  templateUrl: './create-account.component.html',
  styleUrl: './create-account.component.css',
})
export class CreateAccountComponent implements OnInit {
  formData = signal<CreateAccountFormData>({ newPassword: '', confirmPassword: '' });
  creatingAccount = signal(false);
  userKeyID: number | null;
  token: string | null;
  showError = false;
  failureMessage = computed(() => {
    if (!this.passwordsMatch()) return 'Passwords must match!';
    return '';
  });
  capOn = false;
  newPasswordTouched = false;

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    this.capOn = event?.getModifierState('CapsLock');
  }

  passwordsMatch = computed(() => this.formData().newPassword === this.formData().confirmPassword);
  passwordsValid = computed(() => !!this.formData().newPassword && !!this.formData().confirmPassword && this.passwordsMatch());

  private router = inject(Router);
  private accountService = inject(AccountService);
  private route = inject(ActivatedRoute);
  private notificationsService = inject(NotificationsService);

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] || null;
      if (this.token) {
        this.accountService.getCreateAccountData(this.token).subscribe({
          next: (response: ApiDataResponse<CreateAccountData_Result>) => {
            if (response && response.success) {
              this.userKeyID = response.data.UserKeyID || null;
              this.token = response.data.Token || this.token;
            } else {
              this.notificationsService.showError(response?.error || 'Invalid or expired account creation link.');
            }
          },
          error: () => {
            this.notificationsService.showError('An error occurred while validating the account creation link.');
          },
        });
      }
    });
  }

  clearErrors(): void {
    this.showError = false;
  }

  updateFormField(fieldName: keyof CreateAccountFormData, value: string): void {
    this.formData.set({ ...this.formData(), [fieldName]: value });
    this.clearErrors();
  }

  onNewPasswordBlur(): void {
    this.newPasswordTouched = true;
    this.clearErrors();
  }

  createAccount(): void {
    if (!this.userKeyID || !this.token) {
      this.notificationsService.showError('Invalid or missing account creation link.');
      return;
    }
    this.creatingAccount.set(true);
    this.accountService.createAccountFromToken(this.userKeyID, this.formData().newPassword, this.token).subscribe({
      next: (response: ApiDataResponse<CreateAccountData_Result>) => {
        if (response && response.success) {
          window.location.href = '/';
        } else {
          this.notificationsService.showError(response?.error || 'An error occurred while creating the account.');
        }
        this.creatingAccount.set(false);
      },
      error: () => {
        this.notificationsService.showError('An error occurred while creating the account.');
        this.creatingAccount.set(false);
      },
    });
  }
}
